import { DateTime } from '@ksyos/date-time';
import { DatePicker, IconButton, TableRow, TextInput } from '@ksyos/design-system';
import type {
    MedicationRecord,
    Medications,
} from '@frontend/components/pages/customControls/utils/v1/medications/medications';
import type { ValidationErrors } from '@frontend/utils/validation';
import { TableData } from '@frontend/components/elements/common';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

interface MedicationsAddEditTableRowProps {
    errors?: ValidationErrors;
    index?: number;
    medication: MedicationRecord;
    medicationKey: string;
    medications: Medications;
    onChange: (medications: Medications, validationKeys: string[]) => Promise<void>;
}

export function MedicationsAddEditTableRow(props: MedicationsAddEditTableRowProps) {
    const { errors, index, medication, medicationKey, medications, onChange } = props;
    const translate = getSelectedLanguage();
    const validationKeys: string[] = [];

    return (
        <TableRow key={medicationKey}>
            <TableData className="has-textinput">
                <TextInput
                    error={getValidationErrorTranslation(errors?.get(`medications.${index}.name`))}
                    onChange={async ({ target: { value } }) => {
                        await handleChangeMedicationEntryValue(value, 'name');
                    }}
                    placeholder={translate.medications.medicationNamePlaceholder}
                    value={medication.name ?? ''}
                    variant="outline"
                />
            </TableData>
            <TableData className="has-textinput">
                <TextInput
                    error={getValidationErrorTranslation(errors?.get(`medications.${index}.dosageInstruction`))}
                    onChange={async ({ target: { value } }) => {
                        await handleChangeMedicationEntryValue(value, 'dosageInstruction');
                    }}
                    placeholder={translate.medications.medicationPresciptionPlaceholder}
                    value={medication.dosageInstruction ?? ''}
                    variant="outline"
                />
            </TableData>
            <TableData className="has-datepicker">
                <DatePicker
                    error={getValidationErrorTranslation(errors?.get(`medications.${index}.startDate`))}
                    id={`startDate_${index}`}
                    maxValue={
                        medications.getIn([medicationKey, 'endDate']) &&
                        new Date(props.medications.getIn([props.medicationKey, 'endDate']))
                    }
                    onChange={async (value) => {
                        const dateStr = value ? DateTime.from(value).format('YYYY-MM-DD') : null;
                        await handleChangeMedicationEntryValue(dateStr, 'startDate');
                    }}
                    value={medication.startDate ? new Date(medication.startDate) : null}
                />
            </TableData>
            <TableData className="has-datepicker">
                <DatePicker
                    error={getValidationErrorTranslation(errors?.get(`medications.${index}.endDate`))}
                    id={`endDate_${props.index}`}
                    minValue={
                        medications.getIn([props.medicationKey, 'startDate']) &&
                        new Date(medications.getIn([props.medicationKey, 'startDate']))
                    }
                    onChange={async (value) => {
                        const dateStr = value ? DateTime.from(value).format('YYYY-MM-DD') : null;
                        await handleChangeMedicationEntryValue(dateStr, 'endDate');
                    }}
                    value={medication.endDate ? new Date(medication.endDate) : null}
                />
            </TableData>
            <TableData align="right">
                <IconButton
                    icon="delete"
                    onClick={async () => {
                        await onChange(props.medications.delete(props.medicationKey), validationKeys);
                    }}
                    size="small"
                    variant="default"
                />
            </TableData>
        </TableRow>
    );

    async function handleChangeMedicationEntryValue(value: string | Date | null, key: string) {
        if (!validationKeys.includes('medications.' + index + '.' + key)) {
            validationKeys.push('medications.' + index + '.' + key);
        }
        await onChange(props.medications.setIn([medicationKey, key], value), validationKeys);
    }
}
